import React from "react";
import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import CustomTextArea from "../../../../../../components/CustomTextArea";

interface ClientHistoryFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const ClientHistoryForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  viewOnly,
}: ClientHistoryFormProps) => {
  return (
    <Box>
      {/* Patient Demographics Section */}
      <Text variant="subtitle1" fontWeight="500">
        Client History Form
      </Text>

      {/* Main Questionnaire Section */}
      <Box sx={{ mt: 1 }}>
        <Grid container spacing={3}>
          {/* Question 1 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Why are you seeking help now?"
              name="whySeekingHelpNow"
              value={values?.whySeekingHelpNow || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="What is happening or is different? What stressors do you have? What do you hope will be different by seeking help?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 2 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Please give more details about the issue you named above:"
              name="moreDetailsAboutIssue"
              value={values?.moreDetailsAboutIssue || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="When did it start? How often does it happen? How does it affect your life? How have you dealt with it so far?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 3 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Have you ever experienced similar or other mental health symptoms before?"
              name="similarOrOtherSymptomsBefore"
              value={values?.similarOrOtherSymptomsBefore || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="If so, what was your experience like? When did it happen? Did you get help?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 4 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Has anyone in your family ever experienced mental health or substance use issues?"
              name="familyMentalHealthOrSubstanceUse"
              value={values?.familyMentalHealthOrSubstanceUse || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="If so, who was it? Did they seek help or get a diagnosis? What was it like for them? What was it like for you?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 5 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Do you have any current or prior medical issues?"
              name="currentOrPriorMedicalIssues"
              value={values?.currentOrPriorMedicalIssues || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="If so, what was/is it? Have you seen a doctor or other healthcare professional for it? What recommendations or treatment did you have? Is there any family history of disease?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 6 - Medication Field Block */}
          <Grid item xs={12}>
            <Text variant="body1" fontWeight="500" mb={0.5}>
              Are you currently taking any medications - either prescribed or
              over-the-counter?
            </Text>
            <Text
              variant="caption"
              color="textSecondary"
              display="block"
              mb={1}
            >
              If so, please list the name, how much and how often you take it,
              what it's for, and who prescribed it.
            </Text>

            <FormControlLabel
              disabled={viewOnly}
              control={
                <Checkbox
                  disabled={viewOnly}
                  checked={values?.notTakingMedications || false}
                  onChange={(e) =>
                    setFieldValue("notTakingMedications", e.target.checked)
                  }
                  name="notTakingMedications"
                />
              }
              label="I am currently not taking any medications or supplements."
            />

            <Box sx={{ mt: 1.5 }}>
              <Input
                label="Medication or Supplement"
                textFieldProps={{
                  name: "medicationOrSupplementList",
                  value: values?.medicationOrSupplementList || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                  placeholder:
                    "add medication or supplement - search or input custom",
                  disabled: viewOnly,
                }}
              />
            </Box>
          </Grid>

          {/* Question 7 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Do you now, or have you ever, used alcohol, tobacco, recreational drugs, or prescription medication other than as prescribed?"
              name="substanceUseHistoryDetailed"
              value={values?.substanceUseHistoryDetailed || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="If so, which? When did you start, how often did/do you use, and how long did this occur? Please list each substance separately."
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 8 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Who is in your family? What is your relationship with them like?"
              name="familyMembersAndRelationships"
              value={values?.familyMembersAndRelationships || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Please list all individuals you consider to be a part of your family. For those who are not part of your family of origin (such as significant others), please include the duration of your relationship."
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 9 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What social activities and relationships do you engage in?"
              name="socialActivitiesAndRelationships"
              value={values?.socialActivitiesAndRelationships || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="What important social relationships do you have? Do you belong to any social clubs or organizations? How do you like to spend your leisure time?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 10 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What spiritual practices and cultural influences are important to you?"
              name="spiritualPracticesAndCulturalInfluences"
              value={values?.spiritualPracticesAndCulturalInfluences || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Do you belong to a religious, faith, or spiritual community? What other cultural groups do you identify with? How do you celebrate culture and spirituality in your life?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 11 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What was life like as you were growing up, both at home and in school?"
              name="lifeGrowingUpHomeAndSchool"
              value={values?.lifeGrowingUpHomeAndSchool || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Did you meet developmental milestones on time or experience any delays? What were your friends like when you were younger? What was school like for you?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 12 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What significant educational and work/volunteer experiences have you had?"
              name="educationalAndWorkVolunteerExperiences"
              value={values?.educationalAndWorkVolunteerExperiences || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="What is the highest level of education you have completed? Are you currently employed? If so, where and for how long? What other work and educational experiences have you had? Are you satisfied with your current employment and education?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 13 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Do you have any current or prior legal issues?"
              name="currentOrPriorLegalIssues"
              value={values?.currentOrPriorLegalIssues || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Were you ever arrested or charged with a crime or misdemeanor? Do you have any involvement with the civil courts, such as a lawsuit or family law matter? If so, please describe them."
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 14 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What strengths and abilities are you bringing to sessions? What needs or preferences do you have that will help us be successful?"
              name="strengthsAbilitiesAndPreferences"
              value={values?.strengthsAbilitiesAndPreferences || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="What coping skills have been working for you so far? What is important to know that will help make our time more effective for you?"
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>

          {/* Question 15 */}
          <Grid item xs={12}>
            <CustomTextArea
              label="What else is important to know about you?"
              name="whatElseIsImportantToKnow"
              value={values?.whatElseIsImportantToKnow || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Provide any additional information you feel is relevant to your care."
              disabled={viewOnly}
              minRows={3}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientHistoryForm;
